import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { UserService } from '../../core/services/user.service';
import { BranchService } from '../../core/services/branch.service';
import { SessionService } from '../../core/services/session.service';
import { Manager, CreateManagerRequest, UpdateManagerRequest } from '../../core/models/user.model';
import { Branch } from '../../core/models/branch.model';
import { Sport } from '../../core/models/session.model';

@Component({
  selector: 'app-managers',
  imports: [CommonModule, FormsModule, Sidebar],
  templateUrl: './managers.html',
  styleUrl: './managers.scss',
})
export class Managers implements OnInit {
  searchQuery: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 7;
  totalPages: number = 1;
  visiblePages: number[] = [];
  isLoading: boolean = false;

  managers: Manager[] = [];
  filteredManagers: Manager[] = [];

  branches: Branch[] = [];
  sports: Sport[] = [];

  // Modal state
  showModal: boolean = false;
  modalMode: 'add' | 'edit' = 'add';
  isSaving: boolean = false;
  modalError: string = '';
  editingManagerId: string = '';

  form = {
    name: '',
    phoneNumber: '',
    password: '',
    email: '',
    branchIds: [] as string[],
    sportIds: [] as string[],
  };

  // Permissions
  permissions: {
    branchId: string;
    branchName: string;
    sportId: string;
    sportName: string;
    canViewPlans: boolean;
    canEditPlans: boolean;
    canViewSubscriptions: boolean;
    canConfirmPayment: boolean;
    canAddAttendance: boolean;
  }[] = [];
  isSavingPermissions: boolean = false;

  constructor(
    private userService: UserService,
    private branchService: BranchService,
    private sessionService: SessionService
  ) {}

  ngOnInit() {
    this.loadManagers();
    this.loadBranches();
    this.loadSports();
  }

  loadManagers() {
    this.isLoading = true;
    this.userService.getManagers().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.managers = response.data;
          this.filteredManagers = this.managers;
          this.calculatePagination();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading managers:', error);
      }
    });
  }

  loadBranches() {
    this.branchService.getBranches().subscribe({
      next: (response) => {
        if (response.success) this.branches = response.data;
      },
      error: (err) => console.error('Error loading branches:', err)
    });
  }

  loadSports() {
    this.sessionService.getSports().subscribe({
      next: (response) => {
        if (response.success) this.sports = response.data.filter(s => s.isActive);
      },
      error: (err) => console.error('Error loading sports:', err)
    });
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.filteredManagers.length / this.itemsPerPage);
    if (this.totalPages === 0) this.totalPages = 1;
    this.visiblePages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get paginatedManagers() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredManagers.slice(start, end);
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredManagers = this.managers;
    } else {
      this.filteredManagers = this.managers.filter(manager =>
        manager.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (manager.email && manager.email.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        manager.phoneNumber.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
    this.currentPage = 1;
    this.calculatePagination();
  }

  goToPage(page: number) {
    this.currentPage = page;
  }

  getAvatar(name: string): string {
    const colors = ['0B8DD5', 'FF8B3D', '22C55E', 'F59E0B', 'EF4444', '8B5CF6', '06B6D4'];
    const colorIndex = name.length % colors.length;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${colors[colorIndex]}&color=fff`;
  }

  getBranchNames(manager: Manager): string {
    return manager.branchAccess.map(b => b.branchName).join(', ') || 'All Branches';
  }

  getSportNames(manager: Manager): string {
    return manager.sportAccess.map(s => s.sportName).join(', ') || 'All Sports';
  }

  // Modal
  openAddModal() {
    this.modalMode = 'add';
    this.form = { name: '', phoneNumber: '', password: '', email: '', branchIds: [], sportIds: [] };
    this.permissions = [];
    this.modalError = '';
    this.showModal = true;
  }

  openEditModal(manager: Manager) {
    this.modalMode = 'edit';
    this.editingManagerId = manager.id;
    this.form = {
      name: manager.name,
      phoneNumber: manager.phoneNumber,
      password: '',
      email: manager.email || '',
      branchIds: manager.branchAccess.map(b => b.branchId),
      sportIds: manager.sportAccess.map(s => s.sportId),
    };
    this.permissions = [];
    this.modalError = '';
    this.showModal = true;
    this.loadManagerPermissions(manager.id);
  }

  closeModal() {
    this.showModal = false;
    this.modalError = '';
  }

  toggleBranch(id: string) {
    const idx = this.form.branchIds.indexOf(id);
    if (idx >= 0) this.form.branchIds.splice(idx, 1);
    else this.form.branchIds.push(id);
    this.buildPermissionsGrid();
  }

  isBranchSelected(id: string): boolean {
    return this.form.branchIds.includes(id);
  }

  toggleSport(id: string) {
    const idx = this.form.sportIds.indexOf(id);
    if (idx >= 0) this.form.sportIds.splice(idx, 1);
    else this.form.sportIds.push(id);
    this.buildPermissionsGrid();
  }

  isSportSelected(id: string): boolean {
    return this.form.sportIds.includes(id);
  }

  buildPermissionsGrid() {
    const newPermissions: typeof this.permissions = [];
    for (const branchId of this.form.branchIds) {
      const branch = this.branches.find(b => b.id === branchId);
      for (const sportId of this.form.sportIds) {
        const sport = this.sports.find(s => s.id === sportId);
        const existing = this.permissions.find(p => p.branchId === branchId && p.sportId === sportId);
        newPermissions.push({
          branchId,
          branchName: branch?.name || '',
          sportId,
          sportName: sport?.name || '',
          canViewPlans: existing?.canViewPlans ?? true,
          canEditPlans: existing?.canEditPlans ?? false,
          canViewSubscriptions: existing?.canViewSubscriptions ?? true,
          canConfirmPayment: existing?.canConfirmPayment ?? false,
          canAddAttendance: existing?.canAddAttendance ?? false,
        });
      }
    }
    this.permissions = newPermissions;
  }

  loadManagerPermissions(managerId: string) {
    this.userService.getManagerPermissions(managerId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.permissions = res.data;
          this.buildPermissionsGrid();
        }
      },
      error: () => {
        this.buildPermissionsGrid();
      }
    });
  }

  savePermissions() {
    if (!this.editingManagerId) return;
    this.isSavingPermissions = true;
    this.userService.setManagerPermissions(this.editingManagerId, { permissions: this.permissions }).subscribe({
      next: (res) => {
        this.isSavingPermissions = false;
        if (!res.success) {
          this.modalError = res.message || 'Failed to save permissions.';
        }
      },
      error: (err) => {
        this.isSavingPermissions = false;
        this.modalError = err.error?.message || 'Failed to save permissions.';
      }
    });
  }

  submitManager() {
    if (!this.form.name.trim()) {
      this.modalError = 'Name is required.';
      return;
    }

    if (this.modalMode === 'add') {
      if (!this.form.phoneNumber.trim() || !this.form.password.trim()) {
        this.modalError = 'Name, phone number, and password are required.';
        return;
      }
      this.isSaving = true;
      this.modalError = '';

      const request: CreateManagerRequest = {
        name: this.form.name.trim(),
        phoneNumber: this.form.phoneNumber.trim(),
        password: this.form.password.trim(),
        branchIds: [...this.form.branchIds],
        sportIds: [...this.form.sportIds],
      };
      if (this.form.email.trim()) request.email = this.form.email.trim();

      this.userService.createManager(request).subscribe({
        next: (response) => {
          this.isSaving = false;
          if (response.success) {
            this.closeModal();
            this.loadManagers();
          } else {
            this.modalError = response.message || 'Failed to create manager.';
          }
        },
        error: (error) => {
          this.isSaving = false;
          this.modalError = error.error?.message || 'An error occurred.';
        }
      });
    } else {
      this.isSaving = true;
      this.modalError = '';

      const request: UpdateManagerRequest = {
        name: this.form.name.trim(),
        branchIds: [...this.form.branchIds],
        sportIds: [...this.form.sportIds],
      };
      if (this.form.email.trim()) request.email = this.form.email.trim();

      this.userService.updateManager(this.editingManagerId, request).subscribe({
        next: (response) => {
          if (response.success) {
            // Save permissions after updating manager
            if (this.permissions.length > 0) {
              this.userService.setManagerPermissions(this.editingManagerId, { permissions: this.permissions }).subscribe({
                next: () => {
                  this.isSaving = false;
                  this.closeModal();
                  this.loadManagers();
                },
                error: () => {
                  this.isSaving = false;
                  this.closeModal();
                  this.loadManagers();
                }
              });
            } else {
              this.isSaving = false;
              this.closeModal();
              this.loadManagers();
            }
          } else {
            this.isSaving = false;
            this.modalError = response.message || 'Failed to update manager.';
          }
        },
        error: (error) => {
          this.isSaving = false;
          this.modalError = error.error?.message || 'An error occurred.';
        }
      });
    }
  }
}
